import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Wave 3.35 — Shared DatePicker
 *
 * A Shadcn-based DatePicker that ALWAYS displays the selected date in the
 * DD-MM-YYYY format, regardless of the user's browser locale. Internally
 * the value remains an ISO `YYYY-MM-DD` string so all downstream code
 * (API params, state, backend filters) keeps working unchanged.
 *
 * The component is a drop-in replacement for `<Input type="date" />`:
 *   - `value`: string in `YYYY-MM-DD` format (or '' / undefined for empty)
 *   - `onChange(e)`: called with a synthetic event-shaped object whose
 *     `e.target.value` is the new `YYYY-MM-DD` (or '' when cleared)
 *
 * Optionally callers may use `onValueChange(value)` directly for a cleaner API.
 *
 * Note: this picker is **date-only**. For `datetime-local` inputs (which
 * include time) we still use the native control.
 */
export const DatePicker = React.forwardRef(function DatePicker(
  {
    value,
    onChange,
    onValueChange,
    placeholder = "DD-MM-YYYY",
    className,
    disabled = false,
    allowClear = true,
    fromYear,
    toYear,
    maxDate,
    minDate,
    "data-testid": testId,
    ...rest
  },
  ref
) {
  // Convert the incoming ISO string into a Date object usable by react-day-picker.
  const date = React.useMemo(() => {
    if (!value) return undefined;
    // Accept either 'YYYY-MM-DD' or full ISO strings (legacy)
    const truncated = String(value).slice(0, 10);
    const parsed = parse(truncated, "yyyy-MM-dd", new Date());
    return isValid(parsed) ? parsed : undefined;
  }, [value]);

  const display = date ? format(date, "dd-MM-yyyy") : "";

  const emit = React.useCallback(
    (nextIso) => {
      onValueChange?.(nextIso);
      // Mimic the native onChange event shape so callers using
      // `onChange={(e) => setSomething(e.target.value)}` keep working.
      onChange?.({ target: { value: nextIso } });
    },
    [onChange, onValueChange]
  );

  const [open, setOpen] = React.useState(false);

  const handleSelect = (selected) => {
    if (!selected) return;
    const iso = format(selected, "yyyy-MM-dd");
    emit(iso);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    emit("");
  };

  // Build a sensible default year range when none supplied.
  const currentYear = new Date().getFullYear();
  const effectiveFromYear = fromYear ?? currentYear - 10;
  const effectiveToYear = toYear ?? currentYear + 10;

  // Parse minDate/maxDate (accept YYYY-MM-DD strings or Date objects).
  const parsedMaxDate = React.useMemo(() => {
    if (!maxDate) return undefined;
    if (maxDate instanceof Date) return maxDate;
    const p = parse(String(maxDate).slice(0, 10), "yyyy-MM-dd", new Date());
    return isValid(p) ? p : undefined;
  }, [maxDate]);
  const parsedMinDate = React.useMemo(() => {
    if (!minDate) return undefined;
    if (minDate instanceof Date) return minDate;
    const p = parse(String(minDate).slice(0, 10), "yyyy-MM-dd", new Date());
    return isValid(p) ? p : undefined;
  }, [minDate]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            // Match the default <Input> visual footprint so layouts that
            // depend on h-9/h-10 sizing keep their look.
            "h-9 w-full justify-start text-left font-normal bg-white",
            !display && "text-slate-400 font-normal",
            disabled && "cursor-not-allowed opacity-60",
            className
          )}
          {...rest}
        >
          <CalendarIcon className="mr-2 h-4 w-4 opacity-70 shrink-0" />
          <span className={cn("flex-1 truncate", !display && "text-slate-400")}>{display || placeholder}</span>
          {allowClear && display && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Clear date"
              onClick={handleClear}
              onMouseDown={(e) => e.preventDefault()}
              className="ml-1 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleSelect}
          captionLayout="dropdown-buttons"
          fromYear={effectiveFromYear}
          toYear={effectiveToYear}
          fromDate={parsedMinDate}
          toDate={parsedMaxDate}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
});

DatePicker.displayName = "DatePicker";
